import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ThemePalette } from "@angular/material/core";
import { SubscriptSizing } from "@angular/material/form-field";
import { isEmpty } from "lodash";
import { Subject } from "rxjs";

const ERROR_MESSAGES: { [errorCode: string]: any } = {
    required: "Required field",
    min: ({ min }) => `Minimum value allowed is ${min}`,
    max: ({ max }) => `Maximum value allowed is ${max}`,
    dailyAboveGlobalImpressions: ({ global }) => `Cannot be higher than the total impressions (${global})`,
    minlength: ({ requiredLength }) => `${requiredLength} characters required`,
    maxlength: ({ requiredLength }) => `Maximum ${requiredLength} characters allowed`,
    pattern: "The value doesn`t match with the pattern",
    invalidDomain: "Incorrect domain (e.g., example.com, www.example.com)",
    invalidTrackingUrl: "Invalid URL format (e.g., https://example.com/path)",
    matDatepickerParse: "Not valid date",
    duplicated: "Duplicated value",
    invalidBundleId: "Incorrect format for bundle ID",
    maxAttributesOrCliks: "Max daily attributes or max daily Clicks must be set",
    noDecimal: "No decimal values allowed",
    // Fallback for creative name; same text as i18n key "The name can not include the | $ # or @ characters."
    creativeNameForbiddenChars: "The name can not include the | $ # or @ characters.",
};

/**
 * Interface for options
 */
export interface FormFieldOption {
    key: string;
    value: any;
    enabled?: boolean;
}

@Component({
    selector: "app-base-input",
    standalone: true,
    imports: [CommonModule],
    templateUrl: "./base-input.component.html",
})
export class BaseInputComponent implements OnInit, OnChanges, OnDestroy {
    private static readonly ERROR_PRIORITY: string[] = [
        "required",
        "minlength",
        "maxlength",
        "min",
        "max",
        "dailyAboveGlobalImpressions",
        "pattern",
        "invalidDomain",
        "matDatepickerParse",
        "duplicated",
        "invalidBundleId",
        "maxAttributesOrCliks",
        "noDecimal",
        "creativeNameForbiddenChars",
    ];
    @Input() control: any | FormControl = new FormControl();
    @Input() label: string;
    @Input() placeholder!: string;
    @Input() subscriptSizing: SubscriptSizing = "dynamic";
    @Input() isDisabled: boolean = false;
    @Input() color: ThemePalette = "primary";
    @Input() inputClass: string = "";
    @Input() formFieldClass: string = "";
    @Input() labelClass: string = "";
    @Input() sortOptions: boolean = true;
    @Input() options: string[] | any = []; // Input for options
    @Input() cleanOptions: boolean = false;
    public formattedOptions: FormFieldOption[] = []; // Formatted options

    public formControl: FormControl;
    protected _unsubscribeAll: Subject<any> = new Subject<any>();
    @Input() disabled: boolean = false;

    get errorMessage() {
        let errorMessage: string = "Invalid data";
        if (!isEmpty(this.formControl.errors)) {
            const errors = this.formControl.errors;
            const prioritizedCode = BaseInputComponent.ERROR_PRIORITY.find((errorCode: string) => errors[errorCode] !== undefined);
            const errorCode: string = prioritizedCode ?? Object.keys(errors).pop();
            const errorValue = errors[errorCode];
            errorMessage =
                typeof ERROR_MESSAGES[errorCode] === "function"
                    ? ERROR_MESSAGES[errorCode](errorValue)
                    : ERROR_MESSAGES[errorCode] || "Invalid data";
        }

        return errorMessage;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.control) {
            this.formControl = this.control;
        }

        // Synchronize disabled states
        if (changes.disabled) {
            this.isDisabled = this.disabled;
        }

        if (this.isDisabled || this.disabled) {
            this.formControl?.disable({ emitEvent: false });
        } else {
            this.formControl?.enable({ emitEvent: false });
        }

        if (changes.label) {
            // add label to placeholder if dont have value
            this.placeholder = this.placeholder?.length ? this.placeholder : this.label;
        }

        if (changes.options && !isEmpty(this.options)) {
            this.formatOptions();
        }
    }

    ngOnInit(): void {}

    ngOnDestroy() {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Format options
     */
    protected formatOptions() {
        let formattedOptionsTemp: FormFieldOption[] = [];
        if (!this.cleanOptions) {
            if (typeof this.options === "object") {
                // if options is an object
                formattedOptionsTemp = Object.keys(this.options).map((key: string): FormFieldOption => {
                    return {
                        key: key,
                        value: this.options[key],
                    };
                });
            } else if (typeof this.options?.[0] === "string") {
                // if option passed is string
                formattedOptionsTemp = this.options.map((option: string): FormFieldOption => {
                    return {
                        key: option,
                        value: option.charAt(0).toUpperCase() + option.slice(1),
                    };
                });
            }
        } else {
            this.options.forEach((element: FormFieldOption) => {
                let item: FormFieldOption = { key: "", value: "" };
                item.key = element.key ?? "";
                item.value = element.value ?? "";
                formattedOptionsTemp.push(item);
            });
        }

        if (this.sortOptions) {
            // Filtrar elementos undefined o null antes de ordenar
            formattedOptionsTemp = formattedOptionsTemp
                .filter((option) => option?.value != null)
                .sort((a, b) => String(a.value || "").localeCompare(String(b.value || "")));
        }

        this.formattedOptions = formattedOptionsTemp;
    }
}
